import React, { useState, useEffect } from 'react';
import { useAuth } from '../../Context/AuthContext';
import { Link } from 'react-router-dom';
import { getUserProject, UserProjectResponse, getGroupQuizData, ProjectSection } from '../../Services/UserProjectService';

interface GroupSectionsData {
  sections: ProjectSection[];
  loading: boolean;
}

const UserDashboard: React.FC = () => {
  const { user } = useAuth();
  const [project, setProject] = useState<UserProjectResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [groupSections, setGroupSections] = useState<{ [groupId: string]: GroupSectionsData }>({});
  
  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true);
        const userProject = await getUserProject();
        console.log('UserDashboard - project data:', userProject);
        setProject(userProject);
      } catch (err) {
        console.error('Error loading project:', err);
        setError('Error al cargar el proyecto');
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, []);

  // Cargar secciones de cada grupo
  useEffect(() => {
    const fetchGroupSections = async () => {
      if (!project?.groups) return;

      // Inicializar estado de loading para cada grupo
      const initialState: { [groupId: string]: GroupSectionsData } = {};
      project.groups.forEach(group => {
        initialState[group.projectGroupId] = { sections: [], loading: true };
      });
      setGroupSections(initialState);

      // Cargar secciones de cada grupo en paralelo
      project.groups.forEach(async (group) => {
        try {
          const groupData = await getGroupQuizData(group.projectGroupId);
          setGroupSections(prev => ({
            ...prev,
            [group.projectGroupId]: {
              sections: groupData.group.sections || [],
              loading: false
            }
          }));
        } catch (err) {
          console.error(`Error loading sections for group ${group.projectGroupId}:`, err);
          setGroupSections(prev => ({
            ...prev,
            [group.projectGroupId]: {
              sections: [],
              loading: false
            }
          }));
        }
      });
    };

    fetchGroupSections();
  }, [project]);

  if (loading) {
    return (
      <div className="container py-5">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error</h4>
          <p>{error}</p>
        </div>
      </div>
    );
  }
  
  return (
    <>
      {!project ? (
        <div className="container-custom">
          <div className="text-center py-5">
            <div className="mb-3">
              <i className="bi bi-clipboard-x display-4 text-muted"></i>
            </div>
            <p>No hay proyecto disponible actualmente.</p>
            <p className="text-muted">
              Contacta con tu arquitecto para más información.
            </p>
          </div>
        </div>
      ) : (
        <div style={{ position: 'relative', height: 'calc(100vh - 240px)', width: '100%' }}>
          {/* Fixed header with title */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 10,
              backgroundColor: 'white',
              paddingTop: '1rem',
              paddingBottom: '1rem',
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <div className="container-custom">
              <h2 className="h4 mb-0">{project.externalName}</h2>
            </div>
          </div>

          {/* Scrollable area - full width */}
          <div
            className="hide-scrollbar"
            style={{
              height: '100%',
              width: '100%',
              overflowY: 'auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              paddingTop: '5rem'
            }}
          >
            <div className="container-custom">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', paddingBottom: '3rem' }}>
                {project.groups && project.groups.map((group, groupIndex) => {
                  const groupData = groupSections[group.projectGroupId];
                  const sections = groupData?.sections || [];
                  const isLoadingSections = groupData?.loading ?? true;

                  // Función para determinar si una sección está completada
                  const isSectionCompleted = (section: ProjectSection): boolean => {
                    if (!section.contents || section.contents.length === 0) return false;
                    return section.contents.every(content => content.isCompleted);
                  };

                  // Verificar si el grupo anterior está completado
                  const isPreviousGroupCompleted = (): boolean => {
                    if (groupIndex === 0) return true; // Primer grupo siempre accesible
                    const previousGroup = project.groups[groupIndex - 1];
                    const previousGroupData = groupSections[previousGroup.projectGroupId];
                    if (!previousGroupData || previousGroupData.loading) return false;
                    if (previousGroupData.sections.length === 0) return true;
                    return previousGroupData.sections.every(section => isSectionCompleted(section));
                  };

                  const groupAccessible = isPreviousGroupCompleted();

                  return (
                    <div
                      key={group.projectGroupId}
                      style={{
                        backgroundColor: 'var(--color-gray)',
                        padding: '1rem 2.5rem',
                        width: '100%'
                      }}
                    >
                      {/* Título del grupo */}
                      <div className="quiz-group-title" style={{ marginBottom: '0.75rem' }}>
                        {group.groupName || 'Sin título'}
                      </div>

                      {/* Lista de secciones */}
                      {isLoadingSections ? (
                        <div style={{ padding: '0.5rem 0' }}>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          <span style={{ fontSize: '0.9rem', color: '#666' }}>Cargando secciones...</span>
                        </div>
                      ) : sections.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {sections.map((section, sectionIndex) => {
                            const completed = isSectionCompleted(section);

                            // Verificar si la sección anterior está completada
                            const isPreviousSectionCompleted = sectionIndex === 0 || isSectionCompleted(sections[sectionIndex - 1]);

                            // La sección es accesible si el grupo es accesible Y la sección anterior está completada
                            const sectionAccessible = groupAccessible && isPreviousSectionCompleted && !completed;

                            // Secciones completadas sin challenges y con preguntas son reviewable
                            const hasChallenge = section.contents?.some(
                              (c: any) => c.contentType === 'CHALLENGE'
                            );
                            const hasAnswerableContent = section.contents?.some(
                              (c: any) => ['QUESTION_SIMPLE', 'QUESTION_MULTIPLE', 'IMAGE_COMMENT', 'TEXT_SELECTABLE'].includes(c.contentType)
                            );
                            const isReviewable = completed && !hasChallenge && !!hasAnswerableContent;

                            const isClickable = sectionAccessible || isReviewable;

                            const sectionContent = (
                              <div
                                title={!completed && !sectionAccessible ? 'Completa las secciones anteriores primero' : undefined}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '0.75rem 1rem',
                                  backgroundColor: 'white',
                                  borderRadius: '4px',
                                  cursor: isClickable ? 'pointer' : 'default',
                                  transition: 'background-color 0.2s ease',
                                  opacity: (completed && !isReviewable) || (!completed && !sectionAccessible) ? 0.6 : 1
                                }}
                                onMouseEnter={(e) => isClickable && (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                                onMouseLeave={(e) => isClickable && (e.currentTarget.style.backgroundColor = 'white')}
                              >
                                <span style={{ fontSize: '0.95rem' }}>
                                  {section.name || `Sección ${sectionIndex + 1}`}
                                </span>
                                <span style={{
                                  fontSize: '0.8rem',
                                  fontWeight: '500',
                                  textTransform: 'uppercase',
                                  color: completed ? '#28a745' : '#6c757d'
                                }}>
                                  {completed ? 'Hecho' : 'Pendiente'}
                                </span>
                              </div>
                            );

                            return isClickable ? (
                              <Link
                                key={section.id}
                                to={isReviewable
                                  ? `/user/project/${project.projectId}/group/${group.projectGroupId}/section/${sectionIndex}/review`
                                  : `/user/project/${project.projectId}/group/${group.projectGroupId}/section/${sectionIndex}`
                                }
                                className="text-decoration-none"
                                style={{ color: 'inherit' }}
                              >
                                {sectionContent}
                              </Link>
                            ) : (
                              <div key={section.id}>
                                {sectionContent}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div style={{ fontSize: '0.9rem', color: '#666', fontStyle: 'italic' }}>
                          No hay secciones disponibles
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserDashboard;